import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin, Heart, Globe, Landmark, UserCog } from "lucide-react";
import { BrandMarkStatic } from "./Brand";
import { ORG, ORG_ADDRESS_LINE } from "@/lib/org";

const QUICK = [
  ["Home", "/"],
  ["About Us", "/about"],
  ["Ministries", "/ministries"],
  ["Community Outreach", "/outreach"],
  ["Events", "/events"],
  ["Watch Online", "/watch"],
  ["Give", "/give"],
];

const CONNECT = [
  ["Plan Your Visit", "/new-here"],
  ["Prayer Request", "/prayer"],
  ["Volunteer", "/serve"],
  ["Contact", "/contact"],
  ["Meet The Team", "/about#leadership"],
  ["Privacy & Terms", "/privacy"],
];

const SERVICES = [
  ["Sunday", "Worship Experience · 12:30 PM"],
  ["Monday", "The Classroom · 7:30 PM"],
  ["2nd Thursday", "Fire Nights · Monthly"],
  ["4th Sunday", "Prophetic Night South · 7 PM"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function LinkList({ title, items, prefix }) {
  return (
    <div>
      <h4 className="font-display font-bold uppercase text-sm tracking-[0.22em] text-white mb-5">{title}</h4>
      <ul className="flex flex-col gap-3">
        {items.map(([label, to]) => (
          <li key={label}>
            <Link to={to} data-testid={`${prefix}-${slug(label)}`} className="text-sm text-white/40 hover:text-[var(--brand-yellow)] transition-colors">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[var(--bg-deepest)] pt-24 pb-10 text-white">
      <div className="container-x">
        <div className="h-[3px] mb-14 bg-gradient-to-r from-[var(--brand-red)] via-[var(--brand-red)]/40 to-transparent" />

        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-white/[0.06]">
          <div className="lg:col-span-4 md:col-span-2">
            <BrandMarkStatic className="text-2xl md:text-3xl" />
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)]" data-testid="footer-org-name">
              {ORG.legalName} · {ORG.status}
            </p>
            <p className="mt-5 text-white/50 leading-relaxed max-w-md text-sm">
              {ORG.tagline} Spirit-filled, apostolic, and prophetic — reaching souls, rooting disciples,
              releasing destiny, and reforming culture in Chicago and beyond. Formerly {ORG.formerName}.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Facebook, href: "https://www.facebook.com/reformplacechi", l: "Facebook" },
                { icon: Instagram, href: "https://www.instagram.com/reformplacechi", l: "Instagram" },
                { icon: Youtube, href: ORG.youtube, l: "YouTube" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.l}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.l}
                    data-testid={`footer-social-${s.l.toLowerCase()}`}
                    className="w-10 h-10 grid place-items-center border border-white/[0.07] bg-white/[0.03] text-white/50 hover:bg-[var(--brand-red)] hover:text-white hover:border-[var(--brand-red)] transition-colors"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <LinkList title="Explore" items={QUICK} prefix="footer-link" />
          </div>
          <div className="lg:col-span-2">
            <LinkList title="Connect" items={CONNECT} prefix="footer-connect" />
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display font-bold uppercase text-sm tracking-[0.22em] text-white mb-5">Service Times</h4>
            <ul className="flex flex-col gap-4">
              {SERVICES.map(([day, time]) => (
                <li key={day}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-yellow)]">{day}</div>
                  <div className="text-sm text-white/55">{time}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display font-bold uppercase text-sm tracking-[0.22em] text-white mb-5">Contact</h4>
            <ul className="flex flex-col gap-3 text-sm">
              <li>
                <a href={ORG.phoneHref} className="text-white/45 hover:text-white flex items-center gap-2" data-testid="footer-phone">
                  <Phone size={14} className="brand-red" /> {ORG.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${ORG.email}`} className="text-white/45 hover:text-white flex items-center gap-2" data-testid="footer-email">
                  <Mail size={14} className="brand-red" /> {ORG.email}
                </a>
              </li>
              <li>
                <a href={`mailto:${ORG.giveEmail}`} className="text-white/45 hover:text-white flex items-center gap-2" data-testid="footer-give-email">
                  <Heart size={14} className="brand-red" /> {ORG.giveEmail}
                </a>
              </li>
              <li className="text-white/45 flex items-start gap-2" data-testid="footer-address">
                <MapPin size={14} className="brand-red mt-1 shrink-0" />
                <address className="not-italic leading-relaxed">
                  {ORG.address.street}
                  <br />
                  {ORG.address.city}, {ORG.address.state} {ORG.address.zip}
                </address>
              </li>
            </ul>
          </div>
        </div>

        {/* Organization registration block (nonprofit verification) */}
        <div className="py-8 border-b border-white/[0.06] grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="footer-org-info">
          {[
            { icon: Landmark, l: "Organization", v: `${ORG.legalName} — ${ORG.status}`, id: "footer-org-legal" },
            { icon: Landmark, l: "Tax ID / EIN", v: ORG.ein, id: "footer-ein" },
            { icon: UserCog, l: "Administrator Email", v: ORG.adminEmail, id: "footer-admin-email", href: ORG.adminEmail.includes("@") && !ORG.adminEmail.startsWith("[") ? `mailto:${ORG.adminEmail}` : null },
            { icon: Globe, l: "Website / Domain", v: ORG.domain, id: "footer-domain", href: ORG.url },
          ].map((x) => {
            const Icon = x.icon;
            return (
              <div key={x.id} className="flex items-start gap-3">
                <span className="w-9 h-9 min-w-9 grid place-items-center bg-white/[0.03] border border-white/[0.07] brand-red">
                  <Icon size={14} />
                </span>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">{x.l}</div>
                  {x.href ? (
                    <a href={x.href} target={x.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-sm text-white/70 hover:text-[var(--brand-yellow)] break-all" data-testid={x.id}>
                      {x.v}
                    </a>
                  ) : (
                    <div className="text-sm text-white/70 break-words" data-testid={x.id}>{x.v}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/25" data-testid="footer-copyright">
            © {new Date().getFullYear()} {ORG.legalName}. All Rights Reserved. · {ORG_ADDRESS_LINE}
          </p>
          <div className="flex gap-6 text-xs text-white/25">
            <Link to="/privacy" className="hover:text-[var(--brand-yellow)]" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/privacy#terms" className="hover:text-[var(--brand-yellow)]" data-testid="footer-terms">Terms of Use</Link>
            <Link to="/contact" className="hover:text-[var(--brand-yellow)]" data-testid="footer-contact">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
